import initApi from '@/api/initApi';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { handleApiError } from '@/api/handleApiError.ts';
import type { AxiosError } from 'axios';

import type { RequestConfig } from '@/api/Request.ts';

import { ProjectsApi, V1Api } from '@/clients/v1/index.ts';

import type {
  ProjectsApiAddTestingTestingToControlV1ProjectsProjectIdTestingTestsTestingTestIdControlsControlIdPutRequest,
  ProjectsApiFreezeProjectFrameworksUpdateV1ProjectsProjectIdFreezeFrameworksUpdatePatchRequest,
  ProjectsApiGetCommentsProjectsProjectIdCommentsObjectTypeObjectIdGetRequest,
  ProjectsApiGetControlByCodeProjectsProjectIdControlsControlCodeGetRequest,
  ProjectsApiGetControlsByTestingTestV1ProjectsProjectIdTestingTestsTestingTestIdControlsGetRequest,
  ProjectsApiGetLogsProjectsProjectIdLogsObjectIdGetRequest,
  ProjectsApiGetProjectControlsProjectsProjectIdControlsGetRequest,
  ProjectsApiGetProjectControlsV1ProjectsProjectIdControlsGetRequest,
  ProjectsApiGetProjectDashboardProjectsProjectIdDashboardGetRequest,
  ProjectsApiGetProjectFrameworkVersionProjectsProjectIdFrameworksFrameworkCodeVersionsVersionGetRequest,
  ProjectsApiGetProjectProjectsProjectIdGetRequest,
  ProjectsApiGetTestingTestResultsV1ProjectsProjectIdTestingTestsTestingTestIdResultsGetRequest,
  ProjectsApiProjectFrameworksUpdateV1ProjectsProjectIdFrameworksUpdatePostRequest,
  ProjectsApiRemoveControlFromTestingTestV1ProjectsProjectIdTestingTestsTestingTestIdControlsControlIdDeleteRequest,
  V1ApiGetAiSystemLifeCycleDashboardV1ProjectsProjectIdAiSystemLifeCycleDashboardGetRequest,
} from '@/clients/v1/index.ts';

import type {
  Comment,
  Control,
  Logs,
  PageControl,
  PageControlList,
  ProjectAggregator,
  ProjectAiSystemLifecycleDashboard,
  ProjectDashboard,
  TestingTestResultPage,
} from '@/clients/v1/index.ts';
import { updateControlsMap } from '@/utils/hashMaps/controlsHashMap.ts';

export const useProjectsStore = defineStore('projects', () => {
  const project = ref<ProjectAggregator | null>(null);
  const dashboardData = ref<ProjectDashboard | null>(null);
  const lifecycleData = ref<ProjectAiSystemLifecycleDashboard | null>(null);

  const controlsData = ref<PageControlList | null>(null);
  const controlsDataLight = ref<PageControl | null>(null);

  const testingTestResults = ref<TestingTestResultPage | null>(null);
  const testingTestControls = ref<Control[] | null>(null);

  const projectLogs = ref<Logs[]>([]);
  const comments = ref<Comment[]>([]);

  const activeTab = ref('dashboard');
  const tableView = ref<boolean>(true);

  const projectIsLoading = ref(false);
  const conceptsLoading = ref(false);

  // TEMPORARY VARIABLES

  const STRICT_FRAMEWORK = 'MFF-1';

  // Latest request wins.
  let projectRequestId = 0;

  const getProject = async (params: ProjectsApiGetProjectProjectsProjectIdGetRequest) => {
    const requestId = ++projectRequestId;

    try {
      projectIsLoading.value = true;
      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.getProjectProjectsProjectIdGet(params);

      if (!response || requestId !== projectRequestId) return;
      project.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    } finally {
      if (requestId === projectRequestId) projectIsLoading.value = false;
    }
  };

  const freezeFrameworksUpdate = async (
    params: ProjectsApiFreezeProjectFrameworksUpdateV1ProjectsProjectIdFreezeFrameworksUpdatePatchRequest,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response =
        await projectsApi?.freezeProjectFrameworksUpdateV1ProjectsProjectIdFreezeFrameworksUpdatePatch(
          params,
        );

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectFrameworkVersion = async (
    params: ProjectsApiGetProjectFrameworkVersionProjectsProjectIdFrameworksFrameworkCodeVersionsVersionGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response =
        await projectsApi?.getProjectFrameworkVersionProjectsProjectIdFrameworksFrameworkCodeVersionsVersionGet(
          params,
          options,
        );

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateProjectFrameworks = async (
    params: ProjectsApiProjectFrameworksUpdateV1ProjectsProjectIdFrameworksUpdatePostRequest,
    options?: RequestConfig,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      await projectsApi?.projectFrameworksUpdateV1ProjectsProjectIdFrameworksUpdatePost(
        params,
        options,
      );
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectDashboard = async (
    params: ProjectsApiGetProjectDashboardProjectsProjectIdDashboardGetRequest,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.getProjectDashboardProjectsProjectIdDashboardGet(params);
      if (!response || params.projectId !== project.value?.id) return;

      dashboardData.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectLifecycle = async (
    params: V1ApiGetAiSystemLifeCycleDashboardV1ProjectsProjectIdAiSystemLifeCycleDashboardGetRequest,
  ) => {
    try {
      const v1Api = await initApi(V1Api);
      const response =
        await v1Api?.getAiSystemLifeCycleDashboardV1ProjectsProjectIdAiSystemLifeCycleDashboardGet(
          params,
        );

      if (!response || params.projectId !== project.value?.id) return;

      lifecycleData.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectControls = async (
    params: ProjectsApiGetProjectControlsProjectsProjectIdControlsGetRequest,
  ) => {
    try {
      conceptsLoading.value = true;

      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.getProjectControlsProjectsProjectIdControlsGet(params);

      if (!response) return;

      controlsData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    } finally {
      conceptsLoading.value = false;
    }
  };

  const getProjectControlsLight = async (
    params: ProjectsApiGetProjectControlsV1ProjectsProjectIdControlsGetRequest,
    enableLoading: boolean = true,
    updateStore: boolean = true,
  ) => {
    try {
      if (enableLoading) {
        conceptsLoading.value = true;
      }

      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.getProjectControlsV1ProjectsProjectIdControlsGet(params);

      if (!response) return;

      if (updateStore) {
        controlsDataLight.value = response.data;
      }

      updateControlsMap();
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    } finally {
      conceptsLoading.value = false;
    }
  };

  const getControlByCode = async (
    params: ProjectsApiGetControlByCodeProjectsProjectIdControlsControlCodeGetRequest,
  ) => {
    try {
      const controlsApi = await initApi(ProjectsApi);
      const response =
        await controlsApi?.getControlByCodeProjectsProjectIdControlsControlCodeGet(params);
      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getProjectLogs = async (
    params: ProjectsApiGetLogsProjectsProjectIdLogsObjectIdGetRequest,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.getLogsProjectsProjectIdLogsObjectIdGet(params);
      if (!response) return;

      projectLogs.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getComments = async (
    params: ProjectsApiGetCommentsProjectsProjectIdCommentsObjectTypeObjectIdGetRequest,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);

      const response =
        await projectsApi?.getCommentsProjectsProjectIdCommentsObjectTypeObjectIdGet(params);
      if (!response) return;

      comments.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetCommentsAndLogs = () => {
    comments.value = [];
    projectLogs.value = [];
  };

  const associateTestToControl = async (
    params: ProjectsApiAddTestingTestingToControlV1ProjectsProjectIdTestingTestsTestingTestIdControlsControlIdPutRequest,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response =
        await projectsApi?.addTestingTestingToControlV1ProjectsProjectIdTestingTestsTestingTestIdControlsControlIdPut(
          params,
        );
      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getTestingTestControls = async (
    params: ProjectsApiGetControlsByTestingTestV1ProjectsProjectIdTestingTestsTestingTestIdControlsGetRequest,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response =
        await projectsApi?.getControlsByTestingTestV1ProjectsProjectIdTestingTestsTestingTestIdControlsGet(
          params,
        );
      if (!response) return;

      testingTestControls.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const removeTestingTestControl = async (
    params: ProjectsApiRemoveControlFromTestingTestV1ProjectsProjectIdTestingTestsTestingTestIdControlsControlIdDeleteRequest,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      await projectsApi?.removeControlFromTestingTestV1ProjectsProjectIdTestingTestsTestingTestIdControlsControlIdDelete(
        params,
      );
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getTestingTestResults = async (
    params: ProjectsApiGetTestingTestResultsV1ProjectsProjectIdTestingTestsTestingTestIdResultsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response =
        await projectsApi?.getTestingTestResultsV1ProjectsProjectIdTestingTestsTestingTestIdResultsGet(
          params,
          options,
        );
      if (!response) return;

      testingTestResults.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const exportProjectPdf = async (id: string) => {
    try {
      const projectsApi = await initApi(ProjectsApi);
      const response = await projectsApi?.exportProjectV1ProjectsProjectIdExportPost(
        {
          projectId: id,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        {
          responseType: 'blob',
        },
      );

      if (!response) {
        return;
      }

      const data = response.data as unknown as Blob;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.value?.name || ''}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const setView = (view: boolean) => {
    tableView.value = view;
  };

  const resetStore = () => {
    projectRequestId += 1;
    project.value = null;
    lifecycleData.value = null;
    dashboardData.value = null;

    controlsData.value = null;
    controlsDataLight.value = null;

    testingTestResults.value = null;
    testingTestControls.value = null;

    projectLogs.value = [];
    comments.value = [];

    activeTab.value = 'dashboard';
    tableView.value = true;

    projectIsLoading.value = false;
    conceptsLoading.value = false;
  };

  return {
    activeTab,
    comments,
    conceptsLoading,
    controlsData,
    dashboardData,
    exportProjectPdf,
    getComments,
    getControlByCode,
    getProject,
    getProjectControls,
    getProjectDashboard,
    updateProjectFrameworks,
    getProjectLifecycle,
    getProjectLogs,
    getTestingTestResults,
    lifecycleData,
    project,
    projectIsLoading,
    projectLogs,
    resetCommentsAndLogs,
    setView,
    tableView,
    testingTestResults,
    associateTestToControl,
    getTestingTestControls,
    testingTestControls,
    removeTestingTestControl,
    getProjectControlsLight,
    controlsDataLight,
    STRICT_FRAMEWORK,
    getProjectFrameworkVersion,
    freezeFrameworksUpdate,
    resetStore,
  };
});
